/**
 * Modelos del buscador global de proveedores (desplegable del navbar).
 * Backend: LrpProveedores — GET v1/proveedores/busqueda → NLRP.spProveedores_BusquedaGlobal_Consultar.
 */

/** Las cuatro familias que devuelve el buscador. */
export type TipoResultado =
  | 'despacho'
  | 'orden_compra'
  | 'importacion'
  | 'tarifa';

/** Orden en que se pintan los grupos. */
export const TIPOS_RESULTADO: TipoResultado[] = [
  'despacho',
  'orden_compra',
  'importacion',
  'tarifa',
];

/**
 * Por qué coincidió una fila. Cuando NO es `numero` ni `consecutivo`, la tarjeta
 * lo dice explícitamente ("Coincide por BL …"): sin eso, un resultado hallado a
 * través de un BL o una factura parece un error del buscador.
 */
export type MotivoCoincidencia =
  | 'numero'
  | 'consecutivo'
  | 'pedido'
  | 'bl'
  | 'bl_master'
  | 'factura';

/** Motivos que corresponden al identificador propio del objeto. */
const MOTIVOS_PROPIOS: MotivoCoincidencia[] = ['numero', 'consecutivo'];

export const esMotivoPropio = (motivo: MotivoCoincidencia): boolean =>
  MOTIVOS_PROPIOS.includes(motivo);

// ── Contrato del API ────────────────────────────────────────────────────────

export interface ResultadoBusquedaApi {
  tipo: TipoResultado;
  entidadID: number;
  numero: string | null;
  consecutivo: string | null;
  empresaID: number | null;
  empresaNombre: string | null;
  cliente: string | null;
  estadoAbierto: boolean | null;
  faseEtiqueta: string | null;
  faseColor: string | null;
  fecha: string | null;
  relacion: string | null;
  motivoCoincidencia: MotivoCoincidencia;
  valorCoincidencia: string | null;
  ranking: number;
}

export interface ConteoTipoApi {
  tipo: TipoResultado;
  total: number;
}

export interface BusquedaGlobalApi {
  items: ResultadoBusquedaApi[];
  totalCount: number;
  conteos: ConteoTipoApi[];
}

/** Parámetros de la petición. Se serializan tal cual como query string. */
export interface FiltroBusquedaApi {
  q: string;
  empresaId: number | null;
  proveedorId: number | null;
  topPorTipo?: number;
}

// ── Modelo de la UI ─────────────────────────────────────────────────────────

export interface ResultadoBusqueda {
  tipo: TipoResultado;
  id: number;
  /** Número visible. Si el despacho no tiene número cae al consecutivo. */
  numero: string;
  consecutivo: string | null;
  empresaId: number | null;
  empresa: string | null;
  cliente: string | null;
  abierto: boolean;
  fase: string | null;
  faseColor: string | null;
  fecha: Date | null;
  relacion: string | null;
  motivo: MotivoCoincidencia;
  valorCoincidencia: string | null;
  /** true cuando coincidió por algo distinto de su propio número. */
  coincidenciaIndirecta: boolean;
}

/** Un bloque del desplegable: las primeras N filas de una familia. */
export interface GrupoResultados {
  tipo: TipoResultado;
  /** Total de la familia en el alcance, no el número de `items`. */
  total: number;
  items: ResultadoBusqueda[];
}

export interface PaginaBusqueda {
  items: ResultadoBusqueda[];
  grupos: GrupoResultados[];
  totalCount: number;
  conteos: Partial<Record<TipoResultado, number>>;
  /** Mensaje ya traducido; el servicio nunca propaga el error HTTP. */
  error?: string;
}

/** Longitud mínima del término. Por debajo, ni se llama al backend. */
export const MIN_CARACTERES_BUSQUEDA = 3;

export const conteosVacios = (): Record<TipoResultado, number> => ({
  despacho: 0,
  orden_compra: 0,
  importacion: 0,
  tarifa: 0,
});

export const paginaVacia = (error?: string): PaginaBusqueda => ({
  items: [],
  grupos: [],
  totalCount: 0,
  conteos: conteosVacios(),
  error,
});
